import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { api, type Quote, type QuoteStatus } from '../../lib/api'

const statusLabels: Record<QuoteStatus, string> = {
  draft: 'Rascunho', sent: 'Enviado', approved: 'Aprovado', rejected: 'Recusado', cancelled: 'Cancelado',
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value / 100)
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' }).format(new Date(value))
}

function greeting() {
  const hour = new Date().getHours()
  if (hour < 12) return 'Bom dia'
  if (hour < 18) return 'Boa tarde'
  return 'Boa noite'
}

function EmptyOverview() {
  return (
    <section className="dashboard-empty">
      <span aria-hidden="true">↗</span>
      <div>
        <p className="dashboard-kicker">PRIMEIROS PASSOS</p>
        <h2>Sua operação começa com um orçamento.</h2>
        <p>Cadastre um cliente, organize seu catálogo e crie a primeira proposta para alimentar esta visão geral.</p>
      </div>
      <div className="dashboard-empty-actions">
        <Link to="/app/contacts">Cadastrar contato</Link>
        <Link className="primary" to="/app/quotes">Criar orçamento</Link>
      </div>
    </section>
  )
}

export function DashboardPage() {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [activeOrganization, setActiveOrganization] = useState(() => localStorage.getItem('cromaos.activeOrganization') ?? '')

  const currentUser = useQuery({ queryKey: ['current-user'], queryFn: api.currentUser })
  const subscription = useQuery({ queryKey: ['subscription', activeOrganization], queryFn: () => api.subscription(activeOrganization), enabled: Boolean(activeOrganization) })
  const contacts = useQuery({ queryKey: ['dashboard-contacts', activeOrganization], queryFn: () => api.contacts(activeOrganization, { status: 'active', page: 1, pageSize: 1 }), enabled: Boolean(activeOrganization) })
  const quotes = useQuery({ queryKey: ['dashboard-quotes', activeOrganization], queryFn: () => api.quotes(activeOrganization, { page: 1, pageSize: 100 }), enabled: Boolean(activeOrganization) })
  const catalog = useQuery({ queryKey: ['dashboard-catalog', activeOrganization], queryFn: () => api.catalogItems(activeOrganization, { status: 'active', page: 1, pageSize: 100 }), enabled: Boolean(activeOrganization) })
  const components = useQuery({ queryKey: ['dashboard-components', activeOrganization], queryFn: () => api.components(activeOrganization, { status: 'active', page: 1, pageSize: 1 }), enabled: Boolean(activeOrganization) })

  useEffect(() => {
    const organizations = currentUser.data?.organizations ?? []
    const firstOrganization = organizations[0]
    if (firstOrganization && !organizations.some((organization) => organization.id === activeOrganization)) {
      selectOrganization(firstOrganization.id)
    }
  }, [activeOrganization, currentUser.data])

  const createOrganization = useMutation({
    mutationFn: api.createOrganization,
    onSuccess: async (organization) => {
      selectOrganization(organization.id)
      setName('')
      setSlug('')
      await queryClient.invalidateQueries({ queryKey: ['current-user'] })
    },
  })

  const overview = useMemo(() => {
    const items = quotes.data?.items ?? []
    const liveQuotes = items.filter((quote) => quote.status !== 'cancelled')
    const pipeline = liveQuotes.filter((quote) => quote.status === 'draft' || quote.status === 'sent')
    const decided = liveQuotes.filter((quote) => quote.status === 'approved' || quote.status === 'rejected')
    const approved = liveQuotes.filter((quote) => quote.status === 'approved')
    const recent = [...items].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt)).slice(0, 5)
    const averageMargin = liveQuotes.length ? liveQuotes.reduce((total, quote) => total + quote.marginPercent, 0) / liveQuotes.length : 0

    return {
      items,
      recent,
      pipelineCents: pipeline.reduce((total, quote) => total + quote.totalCents, 0),
      approvedCents: approved.reduce((total, quote) => total + quote.totalCents, 0),
      conversion: decided.length ? Math.round((approved.length / decided.length) * 100) : 0,
      averageMargin,
      statusCounts: (['draft', 'sent', 'approved', 'rejected'] as QuoteStatus[]).map((status) => ({ status, count: items.filter((quote) => quote.status === status).length })),
    }
  }, [quotes.data])

  const activeOrganizationName = currentUser.data?.organizations.find((organization) => organization.id === activeOrganization)?.name
  const lowMarginItems = catalog.data?.items.filter((item) => item.marginPercent < 20).length ?? 0
  const sentQuotes = overview.statusCounts.find((item) => item.status === 'sent')?.count ?? 0
  const maxStatusCount = Math.max(1, ...overview.statusCounts.map((item) => item.count))
  const isLoading = currentUser.isPending || (Boolean(activeOrganization) && (quotes.isPending || contacts.isPending))
  const hasError = currentUser.isError || quotes.isError || contacts.isError || catalog.isError || components.isError

  function selectOrganization(id: string) {
    localStorage.setItem('cromaos.activeOrganization', id)
    setActiveOrganization(id)
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    createOrganization.mutate({ name, slug })
  }

  function quoteTitle(quote: Quote) {
    return quote.title || `Orçamento #${quote.number}`
  }

  return (
    <main className="workspace-content dashboard-page">
      <header className="dashboard-header">
        <div>
          <p className="dashboard-kicker">VISÃO GERAL · {new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' }).format(new Date()).toUpperCase()}</p>
          <h1>{greeting()}, <em>vamos ao que importa.</em></h1>
          <p>Acompanhe vendas, margem e pontos de atenção da sua operação.</p>
        </div>
        <div className="dashboard-header-actions">
          {currentUser.data && currentUser.data.organizations.length > 0 && (
            <label className="organization-switcher">
              <span>Empresa ativa</span>
              <select value={activeOrganization} onChange={(event) => selectOrganization(event.target.value)} aria-label="Empresa ativa">
                {currentUser.data.organizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}
              </select>
            </label>
          )}
          <Link className="dashboard-secondary-action" to="/app/contacts">Novo contato</Link>
          <Link className="dashboard-primary-action" to="/app/quotes">Novo orçamento <span aria-hidden="true">→</span></Link>
        </div>
      </header>

      {isLoading && <div className="dashboard-loading" role="status"><span /> Atualizando a visão geral…</div>}
      {hasError && <div className="dashboard-error" role="alert">Não foi possível carregar todos os dados da visão geral. Tente atualizar a página.</div>}

      {!isLoading && currentUser.data?.organizations.length === 0 && (
        <section className="dashboard-onboarding-card">
          <div><p className="dashboard-kicker">CONFIGURAÇÃO</p><h2>Crie sua primeira empresa</h2><p>Ela será o ambiente isolado para seus contatos, catálogo e orçamentos.</p></div>
          <form onSubmit={submit}>
            <label>Nome da empresa<input value={name} onChange={(event) => setName(event.target.value)} minLength={2} maxLength={160} required /></label>
            <label>Identificador<input value={slug} onChange={(event) => setSlug(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} pattern="[a-z0-9]+(?:-[a-z0-9]+)*" required /></label>
            {createOrganization.isError && <p className="error">{createOrganization.error.message}</p>}
            <button disabled={createOrganization.isPending} type="submit">{createOrganization.isPending ? 'Criando…' : 'Criar empresa'}</button>
          </form>
        </section>
      )}

      {activeOrganization && !isLoading && (
        <>
          <section className="dashboard-metrics" aria-label="Indicadores principais">
            <article className="dashboard-metric-card accent"><div><span>Pipeline aberto</span><i aria-hidden="true">↗</i></div><strong>{formatCurrency(overview.pipelineCents)}</strong><p>{sentQuotes} {sentQuotes === 1 ? 'proposta enviada' : 'propostas enviadas'}</p></article>
            <article className="dashboard-metric-card"><div><span>Vendas aprovadas</span><i aria-hidden="true">✓</i></div><strong>{formatCurrency(overview.approvedCents)}</strong><p>No histórico de orçamentos</p></article>
            <article className="dashboard-metric-card"><div><span>Conversão</span><i aria-hidden="true">%</i></div><strong>{overview.conversion}%</strong><p>Entre propostas decididas</p></article>
            <article className="dashboard-metric-card"><div><span>Margem média</span><i aria-hidden="true">◎</i></div><strong>{overview.averageMargin.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%</strong><p className={overview.averageMargin > 0 && overview.averageMargin < 20 ? 'warning' : ''}>{overview.averageMargin > 0 && overview.averageMargin < 20 ? 'Requer atenção' : 'Operação saudável'}</p></article>
          </section>

          {overview.items.length === 0 ? <EmptyOverview /> : (
            <section className="dashboard-grid">
              <article className="dashboard-panel recent-quotes-panel">
                <header><div><p className="dashboard-kicker">COMERCIAL</p><h2>Orçamentos recentes</h2></div><Link to="/app/quotes">Ver todos <span aria-hidden="true">→</span></Link></header>
                <div className="dashboard-quote-list">
                  {overview.recent.map((quote) => (
                    <div className="dashboard-quote-row" key={quote.id}>
                      <span className="dashboard-quote-number">#{String(quote.number).padStart(4, '0')}</span>
                      <div><strong>{quoteTitle(quote)}</strong><span>{quote.contactName} · Atualizado em {formatDate(quote.updatedAt)}</span></div>
                      <strong className="dashboard-quote-value">{formatCurrency(quote.totalCents)}</strong>
                      <span className={`dashboard-status ${quote.status}`}>{statusLabels[quote.status]}</span>
                    </div>
                  ))}
                </div>
              </article>

              <aside className="dashboard-panel attention-panel">
                <header><div><p className="dashboard-kicker">AGORA</p><h2>Pontos de atenção</h2></div><span className="attention-count">{Number(sentQuotes > 0) + Number(lowMarginItems > 0) + Number(subscription.data?.status === 'past_due')}</span></header>
                <div className="attention-list">
                  <Link to="/app/quotes"><i className="blue" aria-hidden="true">→</i><div><strong>{sentQuotes || 'Nenhuma'} {sentQuotes === 1 ? 'proposta aguardando retorno' : 'propostas aguardando retorno'}</strong><span>{sentQuotes ? 'Acompanhe os clientes com proposta enviada.' : 'Nenhum follow-up comercial pendente.'}</span></div></Link>
                  <Link to="/app/catalog"><i className={lowMarginItems ? 'amber' : 'green'} aria-hidden="true">{lowMarginItems ? '!' : '✓'}</i><div><strong>{lowMarginItems || 'Nenhum'} {lowMarginItems === 1 ? 'item com margem baixa' : 'itens com margem baixa'}</strong><span>{lowMarginItems ? 'Revise custo e preço antes do próximo orçamento.' : 'Seu catálogo está com margens saudáveis.'}</span></div></Link>
                  <div><i className={subscription.data?.status === 'past_due' ? 'amber' : 'green'} aria-hidden="true">{subscription.data?.status === 'past_due' ? '!' : '✓'}</i><div><strong>{subscription.data?.status === 'past_due' ? 'Pagamento pendente' : 'Assinatura em dia'}</strong><span>{subscription.data ? `${subscription.data.plan.name} · ciclo até ${formatDate(subscription.data.currentPeriodEnd)}` : 'Consultando informações do plano.'}</span></div></div>
                </div>
              </aside>

              <article className="dashboard-panel pipeline-panel">
                <header><div><p className="dashboard-kicker">FUNIL</p><h2>Distribuição das propostas</h2></div><span>{quotes.data?.total ?? 0} no total</span></header>
                <div className="pipeline-bars">
                  {overview.statusCounts.map((item) => (
                    <div key={item.status}><div><span><i className={item.status} />{statusLabels[item.status]}</span><strong>{item.count}</strong></div><div className="pipeline-track"><span className={item.status} style={{ width: `${(item.count / maxStatusCount) * 100}%` }} /></div></div>
                  ))}
                </div>
              </article>

              <article className="dashboard-panel operation-panel">
                <header><div><p className="dashboard-kicker">BASE OPERACIONAL</p><h2>{activeOrganizationName}</h2></div><Link to="/app/catalog">Gerenciar</Link></header>
                <div className="operation-numbers">
                  <div><span>Contatos ativos</span><strong>{contacts.data?.total ?? 0}</strong></div>
                  <div><span>Itens no catálogo</span><strong>{catalog.data?.total ?? 0}</strong></div>
                  <div><span>Componentes</span><strong>{components.data?.total ?? 0}</strong></div>
                </div>
                {subscription.data && <div className="plan-strip"><div><span>Plano atual</span><strong>{subscription.data.plan.name}</strong></div><span className={`plan-state ${subscription.data.accessState}`}>{subscription.data.accessState === 'full' ? 'Acesso completo' : subscription.data.accessState === 'grace' ? 'Em carência' : 'Acesso bloqueado'}</span></div>}
              </article>
            </section>
          )}

          <details className="organization-management">
            <summary>Empresas e ambientes <span>Gerenciar →</span></summary>
            <div>
              <section><h2>Suas empresas</h2><div className="organization-compact-list">{currentUser.data?.organizations.map((organization) => <button className={activeOrganization === organization.id ? 'active' : ''} key={organization.id} onClick={() => selectOrganization(organization.id)} type="button"><span>{organization.name}<small>{organization.slug} · {organization.role}</small></span><strong>{activeOrganization === organization.id ? 'Ativa' : 'Selecionar'}</strong></button>)}</div></section>
              <form onSubmit={submit}><h2>Criar outra empresa</h2><label>Nome<input value={name} onChange={(event) => setName(event.target.value)} minLength={2} maxLength={160} required /></label><label>Identificador<input value={slug} onChange={(event) => setSlug(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} pattern="[a-z0-9]+(?:-[a-z0-9]+)*" required /></label>{createOrganization.isError && <p className="error">{createOrganization.error.message}</p>}<button disabled={createOrganization.isPending} type="submit">{createOrganization.isPending ? 'Criando…' : 'Criar empresa'}</button></form>
            </div>
          </details>
        </>
      )}
    </main>
  )
}
